import React from 'react'
import { getNotificationBar } from 'api/integrations/storyblok/datasources/getNotificationBar'
import { NotificationBarQuery } from 'api/integrations/storyblok/generated/cms'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { OFFSEASON_SUPPORT_EMAIL } from 'constants/common'
import DocumentPage, { DocumentSection } from 'components/common/DocumentPage'
import ExternalLink from 'components/common/ExternalLink'
import Footer from 'components/landing-page/Footer'
import NavbarLayout from 'components/navbar/NavbarLayout'

const OffseasonLink = () => (
  <ExternalLink
    className="text-blue-600 visited:text-purple-600 hover:underline"
    href="www.theoffseason.io"
  >
    www.theoffseason.io
  </ExternalLink>
)

const AccessibilityStatement = ({
  notificationBar,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <NavbarLayout notificationBar={(notificationBar as NotificationBarQuery).notificationBar?.items}>
    <DocumentPage
      title="Accessibility Statement"
      caption="This is an accessibility statement from The Offseason."
      content={
        <>
          <DocumentSection
            header="Measures to support accessibility"
            content={
              <>
                The Offseason takes the following measures to ensure accessibility of{' '}
                <OffseasonLink />:
                <ul className="list-disc pl-8">
                  <li>Include accessibility as part of our mission statement.</li>
                  <li>Include accessibility throughout our internal policies.</li>
                </ul>
              </>
            }
          />
          <DocumentSection
            header="Conformance status"
            content={
              <>
                <ExternalLink href="https://www.w3.org/WAI/standards-guidelines/wcag/">
                  The Web Content Accessibility Guidelines (WCAG)
                </ExternalLink>{' '}
                defines requirements for designers and developers to improve accessibility for
                people with disabilities. It defines three levels of conformance: Level A, Level AA,
                and Level AAA. <OffseasonLink /> is partially conformant with WCAG 2.1 level AA.
                Partially conformant means that some parts of the content do not fully conform to
                the accessibility standard.
              </>
            }
          />
          <DocumentSection
            header="Feedback"
            content={
              <>
                We welcome your feedback on the accessibility of <OffseasonLink />. Please let us
                know if you encounter accessibility barriers on <OffseasonLink />:
                <ul className="list-disc pl-8">
                  <li>
                    E-mail:{' '}
                    <ExternalLink href={`mailto:${OFFSEASON_SUPPORT_EMAIL}?Subject=Support`}>
                      {OFFSEASON_SUPPORT_EMAIL}
                    </ExternalLink>
                  </li>
                  <li>Visitor Address: 2261 Market Street, Suite 10527, San Francisco, CA 94114</li>
                  <li>Postal Address: P.O. Box 25414, Fresno, CA 93729</li>
                  <li>On social media; Facebook: @theoffseasonusa, Instagram: @theoffseasonusa</li>
                </ul>
                <br />
                We try to respond to feedback within 2 business days.
              </>
            }
          />
          <DocumentSection header="Date" content="This statement was created on 12 October 2023." />
        </>
      }
    />
    <Footer />
  </NavbarLayout>
)

export const getServerSideProps: GetServerSideProps = async () => {
  const notificationBar = await getNotificationBar()

  return {
    props: {
      notificationBar,
    },
  }
}

export default AccessibilityStatement
